import SharedProcessingTimeSection from "@/components/feature/SharedProcessingTimeSection";
import type { ProcessingOption } from "@/components/feature/SharedProcessingTimeSection";

const options: ProcessingOption[] = [
  {
    speed: "2–9",
    unit: "Business Days",
    label: "Fast Passport Center",
    tag: "Most Popular",
    tagColor: "bg-[#F6C344] text-[#1A365D]",
    features: [
      "Registered U.S. Department of State Courier",
      "Reserved agency submission — no flight to Seattle required",
      "Ideal for UAF students & researchers with international fieldwork",
      "Great for Fort Wainwright & Eielson AFB military families on PCS orders",
      "Perfect for Interior Alaska residents who'd otherwise need a $400+ flight",
      "Document review included",
      "Dedicated agent assigned to your case",
      "Daily status updates — never left guessing",
      "Not affected by government shutdowns",
    ],
    highlight: true,
  },
  {
    speed: "3–5",
    unit: "Weeks",
    label: "Fast Passport Center",
    tag: "Standard Expedited",
    tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
    features: [
      "Registered U.S. Department of State Courier",
      "Reserved agency submission",
      "Faster than self-service at Fairbanks post offices",
      "Good for planned international trips from FAI",
      "Document review included",
      "Dedicated agent assigned to your case",
      "Daily status updates — never left guessing",
      "Not affected by government shutdowns",
    ],
    highlight: false,
    smartSaver: true,
  },
  {
    speed: "1–2",
    unit: "Months",
    label: "Self-Service (Post Office)",
    tag: "Slowest",
    tagColor: "bg-gray-100 text-gray-400",
    features: [
      "No courier access — standard mail only via Anchorage sorting hub",
      "No document review — errors cause rejection",
      "In-person clerk appointment required at Fairbanks North Star Borough facilities",
      "Workload shifts with government shutdowns and budget fights",
      "Alaska mail routing adds 1–2 extra weeks through Anchorage — even for expedited",
      "No dedicated agent — you are on your own",
      "Updates only by physical mail letter",
      "No phone or email contact for your case",
      "Cannot upgrade speed after submission",
      "Not suitable if travel is within 12 weeks in Alaska",
    ],
    highlight: false,
    muted: true,
  },
  {
    speed: "24",
    unit: "Hours*",
    label: "Regional Agency Processing",
    tag: "Emergency Only",
    tagColor: "bg-gray-100 text-gray-500",
    features: [
      "Alaska has zero passport agencies — closest is Seattle: flight required, 3+ hours, $400+ round-trip",
      "NPIC appointment required BEFORE booking — no walk-ins anywhere in Alaska",
      "Life-or-death emergencies only — bereavement or medical crisis documentation required",
      "Zero road access to any agency — air travel is the only option from Fairbanks",
      "Mid-winter temperatures of -40°F can cancel flights for days, making emergency travel impossible",
    ],
    highlight: false,
    emergency: true,
  },
];

export default function ProcessingTimeSection() {
  return (
    <SharedProcessingTimeSection
      cityName="Fairbanks"
      headingExtra="AK"
      introText="Fairbanks travelers — UAF students and researchers heading to international field sites, Fort Wainwright and Eielson AFB military families facing PCS orders, oil and resource industry professionals, and Interior Alaska residents who face some of the most extreme passport logistics in the country — have several options for getting a passport fast. Here is how they compare, and why most Alaskans choose a U.S. Department of State registered passport courier to avoid flying to Seattle at $400+ just to apply."
      options={options}
      emergencyDescription="Alaska has zero passport agencies — and Fairbanks is nearly 400 miles north of Anchorage, which itself has no agency. The closest is Seattle, requiring a 3+ hour flight at $400+ round-trip, plus hotel. In winter, Fairbanks temperatures routinely hit -40°F, and ice fog can cancel flights for days — making emergency agency visits literally impossible during cold snaps. You must obtain an NPIC appointment before booking any travel, and there is no road access to any passport agency from anywhere in Alaska."
      disclaimerText="*Processing speed depends on eligibility and documentation. Alaska has no regional passport agency — the closest is Seattle, accessible only by air. Alaska mail routing through Anchorage adds 1–2 weeks to postal timelines. Not all applicants qualify for Regional Agency Processing."
    />
  );
}